/**
 * Sync-run history hook (Phase 2, §7.5) — powers the sync-health panel.
 *
 * GET /api/sync/runs returns the most recent `run_sync` invocations, optionally
 * scoped to one connection. The key embeds the params so a per-connection view
 * and the global view keep separate caches.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk, type SyncRunsQuery } from '../queryKeys.ts';
import type { SyncRunOut } from '../types.ts';

/** Recent sync runs (default 20), newest first. */
export function useSyncRuns(params: SyncRunsQuery = {}): UseQueryResult<SyncRunOut[]> {
  const search = new URLSearchParams();
  search.set('limit', String(params.limit ?? 20));
  if (params.connection_id != null) {
    search.set('connection_id', String(params.connection_id));
  }
  return useQuery({
    queryKey: qk.syncRuns(params),
    queryFn: () => api.get<SyncRunOut[]>(`/api/sync/runs?${search.toString()}`),
    staleTime: 15 * 1000,
  });
}
